import { useEffect, useState } from 'react';
import '../styles/signup.css'; 
import { signIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [resetCode, setResetCode] = useState('');
    const [error, setError] = useState('');
    const [step, setStep] = useState('SIGN_IN'); // SIGN_IN, FORGOT_PASSWORD, CONFIRM_RESET
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Check for pending password reset
        const pendingReset = sessionStorage.getItem('pendingPasswordReset');
        if (pendingReset) {
            const { email: savedEmail, step: savedStep } = JSON.parse(pendingReset);
            setEmail(savedEmail);
            setStep(savedStep);
        }
    }, []);
    
    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        try {
          const { isSignedIn, nextStep } = await signIn({ username: email, password });
    
          if (isSignedIn) {
            sessionStorage.removeItem('pendingPasswordReset');
            window.location.href = '/account';
          } else if (nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
            setStep('REQUIRE_NEW_PASSWORD');
          } else {
            setError(`Next step required: ${nextStep?.signInStep}`);
          }
        } catch (err) {
          setError(err.message || 'Login failed');
        } finally {
          setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            setError('Please enter your email address');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            await resetPassword({ username: email });
            setStep('CONFIRM_RESET');
            sessionStorage.setItem('pendingPasswordReset', JSON.stringify({
                email,
                step: 'CONFIRM_RESET'
            }));
        } catch (error) {
            console.error('Forgot password error:', error);
            if (error.name === 'UserNotFoundException') {
                setError('No account found with this email address');
            } else {
                setError('Error sending reset code. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmReset = async () => {
        if (!resetCode || !newPassword) {
            setError('Please enter both the reset code and new password');
            return;
        }
        
        if (newPassword !== confirmNewPassword) {
            setError('New passwords do not match');
            return;
        }
        
        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }
        
        setLoading(true);
        setError('');
        
        try {
            await confirmResetPassword({
                username: email,
                confirmationCode: resetCode,
                newPassword
            });
            
            sessionStorage.removeItem('pendingPasswordReset');
            alert('Password reset successful! You can now log in with your new password.');
            setStep('SIGN_IN');
            setResetCode('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (error) {
            console.error('Confirm reset error:', error);
            if (error.name === 'CodeMismatchException') {
                setError('Invalid reset code. Please check your email and try again.');
            } else if (error.name === 'InvalidPasswordException') {
                setError('Password does not meet requirements. Please try a stronger password.');
            } else {
                setError('Error resetting password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleBackToSignIn = () => {
        setStep('SIGN_IN');
        setError('');
        sessionStorage.removeItem('pendingPasswordReset');
    };
    
    return (
        <div className="signup">
            <div className="signup-box">
                {step === 'SIGN_IN' && (
                    <>
                        <div className="signup-header">
                            <h1>Welcome Back</h1>
                            <p>Sign in to your Grievers and Friends account</p>
                        </div>
                        {error && <p className="error">{error}</p>}
                        <form onSubmit={handleLogin}>
                            <div className="signup-email">
                                <label htmlFor="email">Email</label>
                                <input
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="signup-password">
                                <label htmlFor="password">Password</label>
                                <input
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                />
                            </div>
                            <button type="submit" className="signup-button" disabled={loading}>
                                {loading ? 'Signing In...' : 'Login'}
                            </button>
                        </form>
                        <div className="signup-footer">
                            <p>Don't have an account? <a href="/signup">Sign up</a></p>
                            <br />
                            <p>
                                <button 
                                    type="button" 
                                    onClick={() => setStep('FORGOT_PASSWORD')} 
                                    className="link-button"
                                >
                                    Forgot Password?
                                </button>
                            </p>
                        </div>
                    </>
                )}

                {step === 'FORGOT_PASSWORD' && (
                    <>
                        <div className="signup-header">
                            <h1>Reset Password</h1>
                            <p>Enter your email to receive a reset code</p>
                        </div>
                        {error && <p className="error">{error}</p>}
                        <div className="signup-email">
                            <label htmlFor="reset-email">Email</label>
                            <input 
                                type="email" 
                                id="reset-email" 
                                value={email} 
                                onChange={e => setEmail(e.target.value)} 
                                required
                            />
                        </div>
                        <button onClick={handleForgotPassword} className="signup-button" disabled={loading}>
                            {loading ? 'Sending...' : 'Send Reset Code'}
                        </button>
                        <div className="signup-footer">
                            <p>
                                <button 
                                    type="button" 
                                    onClick={handleBackToSignIn} 
                                    className="link-button"
                                >
                                    Back to Log In
                                </button>
                            </p>
                        </div>
                    </>
                )}

                {step === 'CONFIRM_RESET' && (
                    <>
                        <div className="signup-header">
                            <h1>Reset Password</h1>
                            <p>Enter the reset code sent to <strong>{email}</strong></p>
                        </div>
                        {error && <p className="error">{error}</p>}
                        <div className="signup-email">
                            <label htmlFor="reset-code">Reset Code</label>
                            <input 
                                type="text" 
                                id="reset-code" 
                                value={resetCode} 
                                onChange={e => setResetCode(e.target.value)} 
                                required
                            />
                        </div>
                        <div className="signup-password">
                            <label htmlFor="new-password">New Password</label>
                            <input 
                                type="password" 
                                id="new-password" 
                                value={newPassword} 
                                onChange={e => setNewPassword(e.target.value)} 
                                required
                            />
                        </div>
                        <div className="signup-password">
                            <label htmlFor="confirm-new-password">Confirm New Password</label>
                            <input 
                                type="password" 
                                id="confirm-new-password" 
                                value={confirmNewPassword} 
                                onChange={e => setConfirmNewPassword(e.target.value)} 
                                required
                            />
                        </div>
                        <button onClick={handleConfirmReset} className="signup-button" disabled={loading}>
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </button>
                        <div className="signup-footer">
                            <p>
                                Didn't receive the code? 
                                <button 
                                    type="button" 
                                    onClick={handleForgotPassword} 
                                    className="link-button"
                                >
                                    Resend Code
                                </button>
                            </p>
                            <br />
                            <p>
                                <button 
                                    type="button" 
                                    onClick={handleBackToSignIn} 
                                    className="link-button"
                                >
                                    Back to Log In
                                </button>
                            </p>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}